import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ALL_TOURS, getTour, TourStep } from '@/lib/onboarding/tours';

interface OnboardingProgress {
  tour_name: string;
  completed: boolean;
  current_step: number;
  skipped: boolean;
}

export interface OnboardingContextType {
  activeTour: string | null;
  currentStep: number;
  totalSteps: number;
  currentStepData: TourStep | null;
  startTour: (tourName: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  resetTour: (tourName: string) => Promise<void>;
  resetAllTours: () => Promise<void>;
  isTourCompleted: (tourName: string) => boolean;
  shouldAutoLaunch: boolean;
  progressMap: Record<string, OnboardingProgress>;
  isLoading: boolean;
  queuedTour: string | null;
  setQueuedTour: (tourName: string | null) => void;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
}

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userId, setUserId] = useState<string | null>(null);
  const [progressMap, setProgressMap] = useState<Record<string, OnboardingProgress>>({});
  const [activeTour, setActiveTour] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [shouldAutoLaunch, setShouldAutoLaunch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [queuedTour, setQueuedTour] = useState<string | null>(null);
  const routeChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load user and progress on mount
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }
      setUserId(user.id);


      const { data } = await supabase
        .from('user_onboarding_progress')
        .select('tour_name, completed, current_step, skipped')
        .eq('user_id', user.id);

      if (data) {
        const map: Record<string, OnboardingProgress> = {};
        data.forEach(row => { map[row.tour_name] = row as OnboardingProgress; });
        setProgressMap(map);
        // Auto-launch if no tours started yet
        setShouldAutoLaunch(data.length === 0);
      } else {
        setShouldAutoLaunch(true);
      }
      setIsLoading(false);
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session?.user) {
        setUserId(null);
        setProgressMap({});
        setShouldAutoLaunch(false);
        setActiveTour(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const saveProgress = useCallback(async (
    tourName: string,
    step: number,
    completed: boolean,
    skipped: boolean,
  ) => {
    if (!userId) return;
    const payload = {
      user_id: userId,
      tour_name: tourName,
      current_step: step,
      completed,
      skipped,
      completed_at: completed ? new Date().toISOString() : null,
    };
    await supabase
      .from('user_onboarding_progress')
      .upsert(payload, { onConflict: 'user_id,tour_name' });
    setProgressMap(prev => ({
      ...prev,
      [tourName]: { tour_name: tourName, completed, current_step: step, skipped },
    }));
  }, [userId]);

  const startTour = useCallback((tourName: string) => {
    const tour = getTour(tourName);
    if (!tour) return;

    const existingProgress = progressMap[tourName];
    const resumeStep = existingProgress && !existingProgress.completed && !existingProgress.skipped
      ? existingProgress.current_step
      : 0;

    setActiveTour(tourName);
    setCurrentStep(resumeStep);

    const step = tour.steps[resumeStep];
    if (step?.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [progressMap, location.pathname, navigate]);

  const currentStepData: TourStep | null = (() => {
    if (!activeTour) return null;
    const tour = getTour(activeTour);
    if (!tour) return null;
    return tour.steps[currentStep] ?? null;
  })();

  const totalSteps = activeTour ? (getTour(activeTour)?.steps.length ?? 0) : 0;

  const nextStep = useCallback(() => {
    if (!activeTour) return;

    // Pause if a dialog is open
    if (document.querySelector('[role="dialog"]')) {
      setTimeout(nextStep, 400);
      return;
    }

    const tour = getTour(activeTour);
    if (!tour) return;

    const nextIdx = currentStep + 1;
    if (nextIdx >= tour.steps.length) {
      // Tour complete
      saveProgress(activeTour, tour.steps.length, true, false);
      setActiveTour(null);
      setCurrentStep(0);
      // Auto-start queued tour if present
      if (queuedTour) {
        const next = queuedTour;
        setQueuedTour(null);
        setTimeout(() => startTour(next), 600);
      }
      return;
    }

    saveProgress(activeTour, nextIdx, false, false);
    setCurrentStep(nextIdx);

    const nextStepData = tour.steps[nextIdx];
    if (nextStepData?.route && location.pathname !== nextStepData.route) {
      navigate(nextStepData.route);
    }
  }, [activeTour, currentStep, location.pathname, navigate, saveProgress]);

  const prevStep = useCallback(() => {
    if (!activeTour || currentStep === 0) return;
    const prevIdx = currentStep - 1;
    const tour = getTour(activeTour);
    setCurrentStep(prevIdx);
    const prevStepData = tour?.steps[prevIdx];
    if (prevStepData?.route && location.pathname !== prevStepData.route) {
      navigate(prevStepData.route);
    }
  }, [activeTour, currentStep, location.pathname, navigate]);

  const skipTour = useCallback(() => {
    if (!activeTour) return;
    saveProgress(activeTour, currentStep, false, true);
    setActiveTour(null);
    setCurrentStep(0);
  }, [activeTour, currentStep, saveProgress]);

  const completeTour = useCallback(() => {
    if (!activeTour) return;
    const tour = getTour(activeTour);
    saveProgress(activeTour, tour?.steps.length ?? 0, true, false);
    setActiveTour(null);
    setCurrentStep(0);
  }, [activeTour, saveProgress]);

  const resetTour = useCallback(async (tourName: string) => {
    if (!userId) return;
    await supabase
      .from('user_onboarding_progress')
      .delete()
      .eq('user_id', userId)
      .eq('tour_name', tourName);
    setProgressMap(prev => {
      const copy = { ...prev };
      delete copy[tourName];
      return copy;
    });
  }, [userId]);

  const resetAllTours = useCallback(async () => {
    if (!userId) return;
    await supabase
      .from('user_onboarding_progress')
      .delete()
      .eq('user_id', userId);
    setProgressMap({});
    setShouldAutoLaunch(true);
  }, [userId]);

  const isTourCompleted = useCallback((tourName: string) => {
    return !!progressMap[tourName]?.completed;
  }, [progressMap]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (routeChangeTimerRef.current) clearTimeout(routeChangeTimerRef.current);
    };
  }, []);

  return (
    <OnboardingContext.Provider value={{
      activeTour,
      currentStep,
      totalSteps,
      currentStepData,
      startTour,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      resetTour,
      resetAllTours,
      isTourCompleted,
      shouldAutoLaunch,
      progressMap,
      isLoading,
      queuedTour,
      setQueuedTour,
    }}>
      {children}
    </OnboardingContext.Provider>
  );
}
